import {
    ChangeDetectionStrategy,
    Component,
    inject,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { NavigationText } from '../../../../../../core/constants/navigation-text';
import { RouteConstants } from '../../../../../../core/constants/routes';
import { AuthenticatedUser } from '../../../../../../core/models/authenticated-user';
import { ReturnRouteService } from '../../../../../../core/services/navigation/return-route.service';
import { NavigationOptions } from '../../../../../../shared/components/page-header-nav/models/navigation-options';
import { PageHeaderNavComponent } from '../../../../../../shared/components/page-header-nav/page-header-nav.component';
import { UserStateService } from '../../../../../user/shared/services/user-state/user-state.service';
import { StepNavigationButtonService } from '../../../../shared/services/step-navigation-button/step-navigation-button.service';
import { OrderDetailsActionButtonVisibilityService } from '../../services/order-details-action-button-visibility/order-details-action-button-visibility.service';
import { OrderDetailsActionService } from '../../services/order-details-action/order-details-action.service';

@Component({
    selector: 'app-order-details-action-bar',
    templateUrl: './order-details-action-bar.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [PageHeaderNavComponent],
})
export class OrderDetailsActionBarComponent implements OnInit, OnDestroy {
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly userStateService = inject(UserStateService);
    private readonly orderDetailsActionService = inject(
        OrderDetailsActionService,
    );
    private readonly stepNavigationButtonService = inject(
        StepNavigationButtonService,
    );
    private readonly orderDetailsActionButtonVisibilityService = inject(
        OrderDetailsActionButtonVisibilityService,
    );
    private readonly returnRouteService = inject(ReturnRouteService);

    private destroy$ = new Subject<void>();

    navigationItems: NavigationOptions = {};
    authenticatedUser: AuthenticatedUser | null =
        this.userStateService.getUser();
    printButtonLabel: string = NavigationText.PRINT;

    ngOnInit(): void {
        this.orderDetailsActionService.printButtonLabel$
            .pipe(takeUntil(this.destroy$))
            .subscribe((printButtonLabel) => {
                this.printButtonLabel = printButtonLabel;
                this.setNavigationLinks();
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    saveChanges(): void {
        this.orderDetailsActionService.triggerSaveChangesClick();
    }

    print(): void {
        this.orderDetailsActionService.triggerPrintClick();
    }

    payment(): void {
        const orderId = this.route.firstChild?.snapshot.paramMap.get('id');
        if (!orderId) {
            this.router.navigate([RouteConstants.Order.rootUrl]);
            return;
        }

        this.router.navigateByUrl(
            `/${RouteConstants.Order.paymentDetails}`.replace(':id', orderId),
        );
    }

    cancel(): void {
        const returnRoute = this.returnRouteService.consume();
        if (returnRoute) {
            this.router.navigate([returnRoute]);
        } else {
            this.router.navigate([RouteConstants.Order.rootUrl]);
        }
    }

    private setNavigationLinks(): void {
        this.setPrimaryLinks();
        this.setSecondaryLinks();
        this.setTertiaryLinks();
    }

    private setPrimaryLinks(): void {
        const primaryLinks = [];

        primaryLinks.push(
            this.stepNavigationButtonService.createPrimaryLink(
                NavigationText.SAVE_CHANGES,
                this.saveChanges.bind(this),
            ),
        );

        this.navigationItems.primaries = primaryLinks;
    }

    private setSecondaryLinks(): void {
        const secondaryLinks = [];

        if (this.authenticatedUser) {
            if (
                this.orderDetailsActionButtonVisibilityService.needsNonElevatedRoleActionButton(
                    this.authenticatedUser.role,
                )
            ) {
                secondaryLinks.push(
                    this.stepNavigationButtonService.createSecondaryLink(
                        this.printButtonLabel,
                        this.print.bind(this),
                    ),
                );
            }

            if (
                this.orderDetailsActionButtonVisibilityService.needsElevatedRoleActionButton(
                    this.authenticatedUser.role,
                )
            ) {
                secondaryLinks.push(
                    this.stepNavigationButtonService.createSecondaryLink(
                        NavigationText.PAYMENT,
                        this.payment.bind(this),
                    ),
                );
            }
        }

        this.navigationItems.secondaries = secondaryLinks;
    }

    private setTertiaryLinks(): void {
        const tertiaryLinks = [];

        if (
            this.authenticatedUser &&
            this.orderDetailsActionButtonVisibilityService.needsElevatedRoleActionButton(
                this.authenticatedUser.role,
            )
        ) {
            tertiaryLinks.push(
                this.stepNavigationButtonService.createTertiaryLink(
                    NavigationText.CANCEL,
                    this.cancel.bind(this),
                ),
            );
        }

        if (
            this.authenticatedUser &&
            this.orderDetailsActionButtonVisibilityService.needsNonElevatedRoleActionButton(
                this.authenticatedUser.role,
            )
        ) {
            tertiaryLinks.push(
                this.stepNavigationButtonService.createTertiaryLink(
                    NavigationText.RETURN_TO_GRID,
                    this.cancel.bind(this),
                ),
            );
        }

        this.navigationItems.tertiaries = tertiaryLinks;
    }
}
